import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Visitor opt-in for new-work notifications.
 *
 * Nothing happens until the visitor presses the button:
 *
 * - The service worker is not registered on page load, so a visitor who never asks
 *   for notifications is never prompted and nothing is installed in their browser.
 * - What they are agreeing to is stated before the browser's own permission prompt
 *   appears, because a bare permission dialog with no context is the reason people
 *   block notifications outright.
 * - The site must have opted in too; this renders nothing otherwise.
 */
export default function NotifyMeButton({ className = '', label = 'Notify me about new work' }) {
    const [state, setState] = useState('checking'); // checking | unavailable | ready | asking | subscribed
    const [error, setError] = useState(null);
    const [explain, setExplain] = useState(false);

    const supported = typeof window !== 'undefined'
        && 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window;

    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (!supported) {
                setState('unavailable');
                return;
            }

            try {
                const res = await fetch('/api/visitor-push', { cache: 'no-store' });
                const data = await res.json();
                if (cancelled) return;

                if (!data.available) {
                    setState('unavailable');
                    return;
                }

                // Already subscribed in this browser?
                const registration = await navigator.serviceWorker.getRegistration('/notifications-sw.js');
                const existing = await registration?.pushManager?.getSubscription();
                if (cancelled) return;

                setState(existing ? 'subscribed' : 'ready');
            } catch {
                if (!cancelled) setState('unavailable');
            }
        })();

        return () => { cancelled = true; };
    }, [supported]);

    const subscribe = useCallback(async () => {
        setState('asking');
        setError(null);

        try {
            const res = await fetch('/api/visitor-push', { cache: 'no-store' });
            const { publicKey } = await res.json();
            if (!publicKey) throw new Error('Notifications are not configured');

            const registration = await navigator.serviceWorker.register('/notifications-sw.js');
            await navigator.serviceWorker.ready;

            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                setState('ready');
                setError('Notifications were not allowed in this browser.');
                return;
            }

            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey),
            });

            const saved = await fetch('/api/visitor-push', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ subscription: subscription.toJSON(), consent: true }),
            });
            if (!saved.ok) throw new Error('Could not save the subscription');

            setState('subscribed');
        } catch (err) {
            setState('ready');
            setError(err.message || 'Could not turn on notifications');
        }
    }, []);

    const unsubscribe = useCallback(async () => {
        try {
            const registration = await navigator.serviceWorker.getRegistration('/notifications-sw.js');
            const subscription = await registration?.pushManager?.getSubscription();

            if (subscription) {
                await fetch('/api/visitor-push', {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ endpoint: subscription.endpoint }),
                });
                await subscription.unsubscribe();
            }
            await registration?.unregister();
            setState('ready');
        } catch (err) {
            setError(err.message || 'Could not turn off notifications');
        }
    }, []);

    if (state === 'checking' || state === 'unavailable') return null;

    if (state === 'subscribed') {
        return (
            <button
                type="button"
                onClick={unsubscribe}
                className={`text-xs underline opacity-70 hover:opacity-100 ${className}`}
            >
                Stop notifications
            </button>
        );
    }

    return (
        <span className={className}>
            {!explain ? (
                <button
                    type="button"
                    onClick={() => setExplain(true)}
                    className="text-xs underline opacity-70 hover:opacity-100"
                >
                    {label}
                </button>
            ) : (
                <span className="inline-flex flex-col gap-1 text-xs">
                    <span className="opacity-70">
                        A notification when a new project is published. No email, nothing shared,
                        and you can stop it at any time.
                    </span>
                    <span className="flex gap-2">
                        <button
                            type="button"
                            onClick={subscribe}
                            disabled={state === 'asking'}
                            className="underline disabled:opacity-50"
                        >
                            {state === 'asking' ? 'Asking...' : 'Yes, notify me'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setExplain(false)}
                            className="underline opacity-60"
                        >
                            No thanks
                        </button>
                    </span>
                    {error && <span className="opacity-80">{error}</span>}
                </span>
            )}
        </span>
    );
}

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
}

NotifyMeButton.propTypes = {
    className: PropTypes.string,
    label: PropTypes.string,
};
